import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  ApiSuccess,
  InvoiceCertificate,
  InvoiceListItem,
  InvoiceListQuery,
  InvoiceView,
  IssueInvoiceInput,
} from '@app/shared';
import { apiClient } from '../../lib/api-client';

const KEY = ['invoices'] as const;

export function useInvoices(query: Partial<InvoiceListQuery>) {
  return useQuery({
    queryKey: [...KEY, 'list', query],
    queryFn: async (): Promise<{
      data: InvoiceListItem[];
      meta: { total: number; page: number; pageSize: number };
    }> => {
      const res = await apiClient.get<{
        data: InvoiceListItem[];
        meta: { total: number; page: number; pageSize: number };
      }>('/invoices', { params: query });
      return res.data;
    },
  });
}

export function useInvoice(id: number | undefined) {
  return useQuery({
    queryKey: [...KEY, 'detail', id],
    enabled: id != null,
    queryFn: async (): Promise<InvoiceView> => {
      const res = await apiClient.get<ApiSuccess<InvoiceView>>(`/invoices/${id}`);
      return res.data.data;
    },
  });
}

export function useIssueInvoice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: IssueInvoiceInput): Promise<InvoiceView> => {
      const res = await apiClient.post<ApiSuccess<InvoiceView>>('/invoices', input);
      return res.data.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: KEY });
    },
  });
}

export function useInvoiceCertificate(id: number | undefined) {
  return useQuery({
    queryKey: [...KEY, 'certificate', id],
    enabled: id != null,
    queryFn: async (): Promise<InvoiceCertificate> => {
      const res = await apiClient.get<ApiSuccess<InvoiceCertificate>>(
        `/invoices/${id}/certificate`,
      );
      return res.data.data;
    },
  });
}
